import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { formatLocale, localInputToUtc } from '../promemoria/tzUtil';
import { usePromemoria } from '../promemoria/PromemoriaContext';

const vuoto = { titolo: '', note: '', dataOra: '', clienteId: '',
  destinatarioTipo: 'tutti', assegnatoA: '', destinatarioRuolo: '' };

export default function PromemoriaPage() {
  const { ricarica } = usePromemoria();
  const [lista, setLista] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [form, setForm] = useState(vuoto);
  const [editId, setEditId] = useState(null);
  const [includiPassati, setIncludiPassati] = useState(false);
  const [caricamento, setCaricamento] = useState(true);
  const [errore, setErrore] = useState('');


  const carica = useCallback(async () => {
    setCaricamento(true);
    try {
      const r = await api.get(`/promemoria?includiPassati=${includiPassati}`);
      setLista(r.data?.data ?? []);
      setErrore('');
    } catch (e) {
      setErrore('Impossibile caricare i promemoria.');
    }
    setCaricamento(false);
  }, [includiPassati]);

  useEffect(() => { carica(); }, [carica]);

  useEffect(() => { api.post('/promemoria/conferme/viste').catch(()=>{}); }, []);

  useEffect(() => {
    api.get('/clienti')
      .then(r => setClienti(r.data?.data ?? []))
      .catch(() => {});
    api.get('/promemoria/destinatari')
      .then(r => setUtenti(r.data?.data ?? []))
      .catch(() => {});
  }, []);

  const salva = async () => {
    if (!form.titolo.trim() || !form.dataOra) {
      setErrore('Titolo e data/ora sono obbligatori.');
      return;
    }
    if (form.destinatarioTipo === 'utente' && !form.assegnatoA) {
      setErrore('Seleziona l\'utente destinatario.'); return;
    }
    if (form.destinatarioTipo === 'ruolo' && !form.destinatarioRuolo) {
      setErrore('Seleziona il gruppo destinatario.'); return;
    }
    const body = {
      titolo: form.titolo.trim(),
      note: form.note?.trim() || null,
      dataOra: localInputToUtc(form.dataOra),
      clienteId: form.clienteId ? Number(form.clienteId) : null,
      destinatarioTipo: form.destinatarioTipo,
      assegnatoA: form.destinatarioTipo === 'utente' && form.assegnatoA
        ? Number(form.assegnatoA) : null,
      destinatarioRuolo: form.destinatarioTipo === 'ruolo'
        ? form.destinatarioRuolo : null,
    };
    try {
      if (editId) await api.put(`/promemoria/${editId}`, body);
      else await api.post('/promemoria', body);
      setForm(vuoto); setEditId(null); setErrore('');
      carica(); ricarica();
    } catch (e) {
      setErrore(e?.response?.data?.error || 'Errore durante il salvataggio.');
    }
  };

  const modifica = (p) => {
    const d = new Date(p.dataOra.endsWith('Z') ? p.dataOra : p.dataOra + 'Z');
    const pad = n => String(n).padStart(2, '0');
    setEditId(p.id);
    setForm({
      titolo: p.titolo,
      note: p.note || '',
      dataOra: `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`,
      clienteId: p.clienteId || '',
      destinatarioTipo: p.destinatarioTipo || 'tutti',
      assegnatoA: p.assegnatoA || '',
      destinatarioRuolo: p.destinatarioRuolo || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const completa = async (p) => {
    try { await api.post(`/promemoria/${p.id}/completa`); carica(); ricarica(); }
    catch (e) { setErrore(e?.response?.data?.error || 'Errore'); }
  };

  const riapri = async (p) => {
    try { await api.post(`/promemoria/${p.id}/riapri`); carica(); ricarica(); }
    catch (e) { setErrore(e?.response?.data?.error || 'Errore'); }
  };

  const elimina = async (p, scope) => {
    const msg = scope === 'tutti'
      ? 'Eliminare questo promemoria per tutti gli utenti?'
      : 'Nascondere questo promemoria solo per te?';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/promemoria/${p.id}?scope=${scope}`);
      carica(); ricarica();
    } catch (e) {
      setErrore(e?.response?.data?.error || "Errore durante l'eliminazione.");
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 1000 }}>
      <h2>Promemoria</h2>

      {errore && <div style={{ background:'#fdecea', color:'#a33', padding:10,
        borderRadius:6, marginBottom:14 }}>{errore}</div>}

      <div style={{ background:'#fafafa', border:'1px solid #e3e3e3', borderRadius:8,
        padding:16, marginBottom:24 }}>
        <h4 style={{ marginTop:0 }}>{editId ? 'Modifica promemoria' : 'Nuovo promemoria'}</h4>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr', gap:12 }}>
          <input placeholder="Titolo *" value={form.titolo}
            onChange={e => setForm({ ...form, titolo: e.target.value })}
            style={{ padding:8 }} />
          <input type="datetime-local" value={form.dataOra}
            onChange={e => setForm({ ...form, dataOra: e.target.value })}
            style={{ padding:8 }} />
          <select value={form.clienteId}
            onChange={e => setForm({ ...form, clienteId: e.target.value })}
            style={{ padding:8 }}>
            <option value="">— Nessun cliente —</option>
            {clienti.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
        </div>
        <textarea placeholder="Note (facoltative)" value={form.note} rows={2}
          onChange={e => setForm({ ...form, note: e.target.value })}
          style={{ width:'100%', padding:8, marginTop:12, boxSizing:'border-box' }} />
        <div style={{ marginTop:12, display:'flex', gap:10, alignItems:'center' }}>
          <span style={{ fontSize:13, color:'#555' }}>Destinatario:</span>
          <select value={form.destinatarioTipo}
            onChange={e => setForm({ ...form, destinatarioTipo: e.target.value,
              assegnatoA:'', destinatarioRuolo:'' })}
            style={{ padding:8 }}>
            <option value="tutti">Tutti</option>
            <option value="utente">Un utente</option>
            <option value="ruolo">Un gruppo</option>
          </select>
          {form.destinatarioTipo === 'utente' && (
            <select value={form.assegnatoA}
              onChange={e => setForm({ ...form, assegnatoA: e.target.value })}
              style={{ padding:8 }}>
              <option value="">— Scegli utente —</option>
              {utenti.map(u => <option key={u.id} value={u.id}>{u.username} ({u.ruolo})</option>)}
            </select>
          )}
          {form.destinatarioTipo === 'ruolo' && (
            <select value={form.destinatarioRuolo}
              onChange={e => setForm({ ...form, destinatarioRuolo: e.target.value })}
              style={{ padding:8 }}>
              <option value="">— Scegli gruppo —</option>
              <option value="admin">Amministratori</option>
              <option value="segreteria">Segreteria</option>
            </select>
          )}
        </div>

        <div style={{ marginTop:12, display:'flex', gap:10 }}>
          <button onClick={salva} style={{ padding:'8px 18px' }}>
            {editId ? 'Salva modifiche' : 'Crea promemoria'}
          </button>
          {editId && (
            <button onClick={() => { setEditId(null); setForm(vuoto); }}
              style={{ padding:'8px 18px' }}>Annulla</button>
          )}
        </div>
      </div>

      <label style={{ display:'block', marginBottom:12 }}>
        <input type="checkbox" checked={includiPassati}
          onChange={e => setIncludiPassati(e.target.checked)} />{' '}
        Mostra anche i promemoria più vecchi di 30 giorni
      </label>

      {caricamento ? <p>Caricamento…</p> : lista.length === 0 ? (
        <p style={{ color:'#888' }}>Nessun promemoria.</p>
      ) : (
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'#f0f0f0', textAlign:'left' }}>
              <th style={{ padding:8 }}>Data/ora</th>
              <th style={{ padding:8 }}>Titolo</th>
              <th style={{ padding:8 }}>Cliente</th>
              <th style={{ padding:8 }}>Destinatario</th>
              <th style={{ padding:8 }}>Stato</th>
              <th style={{ padding:8 }}>Note</th>
              <th style={{ padding:8, width:200 }}>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {lista.map(p => {
              const scaduto = new Date((p.dataOra.endsWith('Z') ? p.dataOra : p.dataOra + 'Z')) < new Date();
              const puoTutti = p.puoEliminareTutti;
              return (
                <tr key={p.id} style={{ borderBottom:'1px solid #eee',
                  background: p.completato ? '#f0fdf4' : (scaduto ? '#fff8e1' : undefined) }}>
                  <td style={{ padding:8, whiteSpace:'nowrap' }}>{formatLocale(p.dataOra)}</td>
                  <td style={{ padding:8 }}>{p.titolo}</td>
                  <td style={{ padding:8 }}>{p.clienteNome || '—'}</td>
                  <td style={{ padding:8, fontSize:13 }}>
                    {p.destinatarioLabel}
                    {p.destinatarioTipo !== 'tutti' && (
                      <span style={{ marginLeft:6, fontSize:11, color:'#888' }}>
                        (non ricevi l'avviso)
                      </span>
                    )}
                  </td>
                  <td style={{ padding:8, fontSize:12 }}>
                    {p.completato ? (
                      <span style={{ color:'#16a34a' }}>
                        ✓ Fatto{p.completatoDaNome ? ` da ${p.completatoDaNome}` : ''}
                        {p.completatoIl ? <><br/><span style={{color:'#888',fontSize:11}}>
                          {formatLocale(p.completatoIl)}</span></> : null}
                      </span>
                    ) : <span style={{ color:'#999' }}>—</span>}
                  </td>
                  <td style={{ padding:8, whiteSpace:'nowrap' }}>
                    {!p.completato
                      ? <button onClick={() => completa(p)} style={{ marginRight:6, color:'#16a34a' }}>✓ Fatto</button>
                      : <button onClick={() => riapri(p)} style={{ marginRight:6 }}>Riapri</button>}
                    <button onClick={() => modifica(p)} style={{ marginRight:6 }}>Modifica</button>
                    <button onClick={() => elimina(p, 'me')} style={{ marginRight:6 }}>Solo me</button>
                    {puoTutti && (
                      <button onClick={() => elimina(p, 'tutti')}
                        style={{ color:'#c9302c' }}>Tutti</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
